/** BACKYARD WELDING PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
  /** Every project in the volume, as printed in the PDF index. */
  projectList: string[]
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/741dd666-b287-481a-b0dc-d10ba348c13b/`

export const PRODUCT = {
  name: 'BACKYARD WELDING PLANS™',
  wordmarkA: 'Backyard',
  wordmarkB: 'Welding Plans',
  descriptor: 'Outdoor Metal Project Plans to Build & Sell',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build for your own backyard — or build and sell the finished pieces.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 BACKYARD PROJECTS',
  '6 DIGITAL VOLUMES',
  'DIMENSIONS & CUT LISTS',
  'BUILD & SELL CARDS',
  '5 FREE BONUS RESOURCES',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** All plan pages are square (1:1). */
const PAGE_SIZE = { width: 1100, height: 1100 }

const COVERS = {
  v1: `${IMG}1792750283884_bwp-vol-1-3532daec.webp`,
  v2: `${IMG}1792750528150_bwp-vol-2-986c2f43.webp`,
  // Volumes 03–06: original covers supplied by the client (square, 1:1).
  v3: 'https://cdn.magicpatterns.com/uploads/dL489WjYFHb5b7iZ5JVG63/Portada_3.png',
  v4: 'https://cdn.magicpatterns.com/uploads/uoRBxRukFvdtPCc8F9U7mj/Portada_4.jpg',
  v5: 'https://cdn.magicpatterns.com/uploads/aRVzWgmTqWV8fmYQA8aNw7/Portada_5.jpg',
  v6: 'https://cdn.magicpatterns.com/uploads/gPjzXaw5nYai3FW5B5caP8/Portada_6.jpg',
}

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792766752119_bwp-hero-mockup-70bca040.webp`,
  alt: 'BACKYARD WELDING PLANS™ volumes on a steel workbench with a dimensioned plan on a tablet, a printed cut list and a backyard fire pit behind',
  width: 928,
  height: 1152,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Backyard Projects', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Dimensions + Cut Lists', icon: 'dimensions' },
  { label: '5 Free Bonus Resources', icon: 'bonus' },
]

/** Project P01, Volume 01, PDF pages 06–11 — the complete project, in order. */
export const PREVIEW_PROJECT = {
  code: 'P01',
  name: 'Square Plate Fire Pit',
  volume: 'Volume 01',
} as const

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: `${IMG}1792768072676_bwp-p01-01-finished-e7754b19.webp`,
    caption: 'The finished build',
    alt: 'Volume 01, page 06: P01 Square Plate Fire Pit finished build — 30 in / 762 mm square, 14 in / 355.6 mm high',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768078353_bwp-p01-02-plan-7141de5f.webp`,
    caption: '3-view dimensioned plan — inches & millimeters',
    alt: 'Volume 01, page 07: P01 front, side and top views with every dimension in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768083727_bwp-p01-03-cutlist-7f8fec7e.webp`,
    caption: 'Exploded view & complete cut list',
    alt: 'Volume 01, page 08: P01 exploded view and cut list for parts A to D in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768089282_bwp-p01-04-steps-c2928fe9.webp`,
    caption: 'Step-by-step fabrication',
    alt: 'Volume 01, page 09: P01 eight-step illustrated fabrication sequence',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768094614_bwp-p01-05-welds-aef5ccd4.webp`,
    caption: 'Weld map & fabrication details',
    alt: 'Volume 01, page 10: P01 weld map with seam locations, tack order and finish checklist',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768100519_bwp-p01-06-sell-card-db5180a4.webp`,
    caption: 'Build & Sell card',
    alt: 'Volume 01, page 11: P01 Build & Sell card with finished size, materials and maker notes',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = ['Cut the steel.', 'Weld it square.', 'Finish it and sell it.']

export const TECH_PROOF: TechProof[] = [
  {
    src: `${IMG}1792768106680_bwp-tech-01-p02-plan-d1354be8.webp`,
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 02 · Hexagon Fire Pit · Volume 01',
    description: 'Every view dimensioned before you cut a single part.',
    alt: 'Volume 01: P02 Hexagon Fire Pit three-view dimensioned plan',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768112492_bwp-tech-02-p03-cutlist-86e58dfb.webp`,
    title: 'KNOW WHAT TO CUT',
    project: 'Project 03 · Fire Pit Log Grate · Volume 01',
    description: 'Parts, quantities, stock and exact cut sizes in one table.',
    alt: 'Volume 01: P03 Fire Pit Log Grate exploded view and complete cut list',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768118245_bwp-tech-03-p04-welds-8593770d.webp`,
    title: 'KNOW WHERE TO WELD',
    project: 'Project 04 · Swing-Arm Campfire Cooking Grate · Volume 01',
    description: 'Weld map, tack order and fit-up details for every joint.',
    alt: 'Volume 01: P04 Swing-Arm Campfire Cooking Grate weld map and fabrication details',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792768128204_bwp-tech-04-p05-steps-d04786ad.webp`,
    title: 'FOLLOW THE BUILD',
    project: 'Project 05 · Fireside Tool Stand · Volume 01',
    description: 'An illustrated sequence from cut parts to the finished piece.',
    alt: 'Volume 01: P05 Fireside Tool Stand step-by-step fabrication',
    ...PAGE_SIZE,
  },
]

const COVER_SIZE = { width: 1100, height: 1100 }

export const VOLUMES: Volume[] = [
  {
    number: 'VOLUME 01',
    title: 'Fire Pits & Fire Features',
    projects: '5 projects',
    description: 'Fire pits, grates and fireside gear — the best-selling backyard builds.',
    projectList: [
      'P01 — Square Plate Fire Pit',
      'P02 — Hexagon Fire Pit',
      'P03 — Fire Pit Log Grate',
      'P04 — Swing-Arm Campfire Cooking Grate',
      'P05 — Fireside Tool Stand',
    ],
    src: COVERS.v1,
    alt: 'Volume 01 cover: Fire Pits & Fire Features',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 02',
    title: 'Outdoor Cooking Stations',
    projects: '5 projects',
    description: 'Grills, griddle carts and prep tables for outdoor kitchens.',
    projectList: [
      'P06 — Charcoal Grill Box on Legs',
      'P07 — Griddle Cart',
      'P08 — Dutch Oven Cooking Table',
      'P09 — Outdoor Grill Prep Table',
      'P10 — Grill Tool & Towel Rack',
    ],
    src: COVERS.v2,
    alt: 'Volume 02 cover: Outdoor Cooking Stations',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 03',
    title: 'Firewood Storage & Handling',
    projects: '5 projects',
    description: 'Log racks, a kindling-shelf rack, a firewood cart and wall brackets.',
    projectList: [
      'P11 — Outdoor Firewood Rack',
      'P12 — Firewood Rack with Kindling Shelf',
      'P13 — Porch Log Holder',
      'P14 — Firewood Cart',
      'P15 — Wall-Mounted Firewood Brackets',
    ],
    src: COVERS.v3,
    alt: 'Volume 03 cover: Firewood Storage & Handling',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 04',
    title: 'Garden & Planter Projects',
    projects: '5 projects',
    description: 'Raised beds, trellises, a garden arch and plant stands.',
    projectList: [
      'P16 — Steel Raised Garden Bed',
      'P17 — Garden Trellis Panel',
      'P18 — Garden Arch',
      'P19 — Hose Reel Stand',
      'P20 — Three-Tier Plant Stand',
    ],
    src: COVERS.v4,
    alt: 'Volume 04 cover: Garden & Planter Projects',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 05',
    title: 'Yard & Patio Accessories',
    projects: '5 projects',
    description: 'Quick, low-cost builds that sell in batches.',
    projectList: [
      'P21 — Boot Scraper & Brush Stand',
      'P22 — Five-Bike Rack',
      'P23 — Mailbox Post',
      'P24 — Outdoor Hook Rail',
      'P25 — Bird Feeder Pole with Two Arms',
    ],
    src: COVERS.v5,
    alt: 'Volume 05 cover: Yard & Patio Accessories',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 06',
    title: 'Gates, Screens & Fence Accents',
    projects: '5 projects',
    description: 'Garden gates, privacy screens, a trash can corral and fence panels.',
    projectList: [
      'P26 — Garden Walk Gate',
      'P27 — Privacy Screen Panel',
      'P28 — Freestanding Privacy Screen',
      'P29 — Trash Can Corral',
      'P30 — Fence Panel with Stakes',
    ],
    src: COVERS.v6,
    alt: 'Volume 06 cover: Gates, Screens & Fence Accents',
    ...COVER_SIZE,
  },
]

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Steel Purchase & Cut List Planner',
    description: 'Buy the right steel once, cut it in order and label every part.',
    src: 'https://cdn.magicpatterns.com/uploads/nVjqCHBiWJdacZy6bc178d/mockup-steel-purchase-cut-list-planner.png',
    alt: 'Bonus 01: Steel Purchase & Cut List Planner',
    ...COVER_SIZE,
  },
  {
    number: '02',
    title: 'Weekend Batch-Build Planner',
    description: 'Build three, five or ten of the same project in one weekend.',
    src: 'https://cdn.magicpatterns.com/uploads/mi2d4qM4uK7qM26UXn8biP/mockup-weekend-batch-build-planner.png',
    alt: 'Bonus 02: Weekend Batch-Build Planner',
    ...COVER_SIZE,
  },
  {
    number: '03',
    title: 'Outdoor Finishing & Protection Guide',
    description: 'From raw steel to a finish that looks good in the yard and in the photo.',
    src: 'https://cdn.magicpatterns.com/uploads/b9GiN7Kx5fqfoZeHvtkHYx/mockup-outdoor-finishing-protection-guide.png',
    alt: 'Bonus 03: Outdoor Finishing & Protection Guide',
    ...COVER_SIZE,
  },
  {
    number: '04',
    title: 'Local Selling Kit',
    description: 'Listing templates, photo setups and simple quotes for selling your builds.',
    src: 'https://cdn.magicpatterns.com/uploads/nqU4rD6yVm4FLsKd2fSSH9/mockup-local-selling-kit-listings-photos-quotes.png',
    alt: 'Bonus 04: Local Selling Kit — Listings, Photos & Quotes',
    ...COVER_SIZE,
  },
  {
    number: '05',
    title: 'Project Costing & Pricing Workbook',
    description: 'Know what each build really costs before you set a price.',
    src: 'https://cdn.magicpatterns.com/uploads/2XUKrJBHPz8QXkMqfynm5m/mockup-project-costing-pricing-workbook.png',
    alt: 'Bonus 05: Project Costing & Pricing Workbook',
    ...COVER_SIZE,
  },
]

export const OFFER_FEATURES: string[] = [
  '3-view dimensioned plans — every measurement in inches and millimeters',
  'Complete cut lists, exploded views and step-by-step fabrication',
  'Weld maps and a Build & Sell card for every project',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of outdoor metal project plans: 30 projects across six PDF volumes, plus five bonus resources.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'Hobby and professional welders who already know how to weld and want ready-to-build backyard projects — for their own home or to sell locally.',
  },
  {
    question: 'Is this a beginner welding course?',
    answer:
      'No. The collection gives you the plans: dimensions, cut lists, drawings, build steps and weld maps. It does not teach welding from the beginning.',
  },
  {
    question: 'What kinds of projects are included?',
    answer:
      'Fire pits and fire features, grills and outdoor cooking stations, firewood racks and carts, garden and planter projects, yard accessories, and gates, privacy screens and fence panels.',
  },
  {
    question: 'Are the plans in inches or millimeters?',
    answer: 'Both. Every dimension on every project page is printed in inches and millimeters.',
  },
  {
    question: 'Are these physical books or equipment?',
    answer:
      'The product is digital. You receive PDF files by email right after payment; steel, tools and equipment are not included.',
  },
  {
    question: 'Can I preview the plans?',
    answer:
      'Yes. All six pages of Project P01 — Square Plate Fire Pit, from Volume 01, are in the preview section above.',
  },
  {
    question: 'Can I sell the projects I build from these plans?',
    answer:
      'Yes. Your purchase includes permission to build projects from these plans for your own use or to sell the physical pieces you make. You may print working copies for your own use. You may not share, resell, redistribute or sublicense the digital plans, PDFs, images or bonus files.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `7-day guarantee: if you have any problem with your purchase, email ${SUPPORT_EMAIL}.`
